/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from 'react';
import {
	Button,
	FormControl,
	IconButton,
	InputLabel,
	OutlinedInput,
	TextField,
	Typography,
  Tooltip,
} from '@mui/material';
import { getOwnershipFinancials, getTotalOwnershipFinancials } from '../services/ownership.service';
import {
	formatCurrency,
	formatDate,
	formatShortDateTime,
	isEmptyString,
	isNullUndefined,
	tryParseFloat,
	sortJSONArray
} from '../util/util';
import { getLeaseFinancials, getTotalLeaseFinancials } from '../services/lease.service';
import { getMeterBillingFinancials, getTotalMeterBillingFinancials } from '../services/meterbilling.service';
import { getClientFinancials, getTotalClientFinancials } from '../services/client.service';
import { editReadings, getMeterFinancials, getTotalMeterFinancials } from '../services/meters.service';
import Delete from '@mui/icons-material/Delete';
import {
	printCharge,
	printClientCharge,
	printClientCredit,
	printClientInvoice,
	printClientReceipt,
	printCredit,
	printInvoice,
	printPaymentVoucher,
	printReceipt,
} from '../services/print.service';
import ModalContainerModule from './modules/modalContainer';
import { DetailsContainer } from '../constants/component.constants';
import { issueResponseMessage, issueWarnMessage } from '../actions/message';
import { ResponsiveRow } from '../constants/layout.constants';
import { useSelector } from 'react-redux';
import { getCustomerFinancials } from '../services/customers.service';
import MuiDataGrid from './modules/MuiDataGrid';

/**
 * This is a shared TransactionHistory component to render history for invoices, charges, receipts and credits to:
 * - Meter Billing view Page
 * - Lease view Page
 * - Ownership view Page
 * - Customer view Page
 * - Client view Page
 * - This also apply to their respective terminated view pages.
 *
 * Required props are:
 * - transactionMode: This is actual page you are implementing this from. Supported modes are Lease, Ownership, Meter Billing, Customer and Client
 * - contract: This is the data for either selected item of 'Lease, Ownership, Meter Billing, Customer or Client'
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 * @author jmbuthia
 *  @description If status is voided the row will have different colour.
 */
export default function TransactionHistory(props) {
	const user = useSelector((state) => state.auth.user);
	const [financials, setFinancials] = useState([]);
	const [currentBalance, setCurrentBalance] = useState(0);
	const [isBulkWaterMeter, setIsBulkWaterMeter] = useState(false);
	const [editMeterReadingModal, setEditMeterReadingModal] = useState(false);
	const [currentReading, setCurrentReading] = useState('');
	const [readingId, setReadingId] = useState('');
	const [newReading, setNewReading] = useState('');
	const [itemList, setItemList] = useState([])
	const [loading, setLoading] = useState(false)
	const [totalRows, setTotalRows] = useState(0);

	const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });


	const meterColumns = useMemo(
		() => {
			const arr = []
			if(props.transactionMode === 'Meters') {
				arr.push(
					{
						headerName: "Name",
						field: "name",
						flex: 1,
						width: 200
					}
				)

				if(isBulkWaterMeter) {
					arr.push(
						{
							headerName: "Type",
							field: "type",
							flex: 1,
							width: 100,
							valueFormatter: (row) => {
								return row.value === "Credit" ? "Invoice" : row.type
							}
						}
					)

					arr.push(
						{
							headerName: "Ref Number",
							field: "refNo",
							flex: 1,
							width: 100,
							renderCell: (value) => {
								const financial = value.row
								const isInvoice = financial?.type === 'Invoice' || financial?.type === "Credit";

								return (
									<Button
										variant="text"
										onClick={() => {
											handlePrintDocument(
												isInvoice ? 'Invoice' : 'Receipt',
												financial.invoiceOrReceiptId
											)}
										}
									>
										{financial.refNo}
									</Button>
								)
							}
						}
					)

					arr.push(
						{
							headerName: "Opening Reading",
							field: "openingReading",
							width: 185,
						}
					)

					arr.push(
						{
							headerName: "Closing Reading",
							field: "closingReading",
							minWidth: 185,
						}
					)
				}

				arr.push(
					{
						headerName: "Time",
						field: "time",
						minWidth: 100,
						flex: 1,
					}
				)

				if(isBulkWaterMeter) {
					arr.push(
						{
							headerName: "Quantity (Litres)",
							field: "quantity",
							minWidth: 100,
							flex: 1,
							valueFormatter: (row) => {
								return row.value !== undefined ? row.value : "-";
							},
						}
					)
				}

				arr.push(
					{
						headerName: "Amount",
						field: "amount",
						minWidth: 100,
						flex: 1,
						valueFormatter: (row) => {
							return formatCurrency(row.value);
						},
					}
				)
			}

			return arr
		},
	)


	const columns = [
		{
			headerName: "Date",
            field: "date",
            minWidth: 150,
            flex: 1,
            valueFormatter: (row) => {
				return formatDate(row.value);
            },
        },
		{
			headerName: "Type",
			field: "type",
			flex: 1,
			width: 150
		},
		{
			headerName: "Ref Number",
			field: "refNo",
			flex: 1,
			width: 150,
			renderCell: (value) => {
				const financial = value.row
				const isInvoice = financial && financial.type === 'Invoice';
				const isReceipt = financial && financial.type === 'Receipt';
				const isCredit = financial && financial.type === 'Credit';
				const isCharge = financial && financial.type === 'Charge';
				const isPaymentVoucher = financial && financial.type === 'Payment Voucher';


				const renderButton = (docType) =>
				  <Button
					variant="text"
					onClick={() => handlePrintDocument(docType, financial.id)}
				  >
					{props.transactionMode === 'Client' ? financial.refNumber : financial.referenceNumber}
				  </Button>

				if (isInvoice) return renderButton('Invoice');
				if (isReceipt) return renderButton('Receipt');
				if (isCredit) return renderButton('Credit');
				if (isCharge) return renderButton('Charge');
				if (isPaymentVoucher)
				  return (
					<Button
					  variant="text"
					  onClick={() => handlePrintDocument('Payment Voucher', financial.id)}
					>
					  {financial.referenceNumber}
					</Button>
				  );

				return financial.referenceNumber;
			  }
		},

		{
			headerName: "Details",
			field: "details",
			flex: 2.5,
			width: 150
		},
		{
			headerName: "Amount Due",
			field: "amountDue",
			flex: 1,
			width: 150
		},
		{
			headerName: "Amount Paid",
			field: "amountPaid",
			flex: 1,
			width: 150
		},
		...(!props.hideVoid && user.proxyRole !== "OWN" ? [{
			headerName: "Void",
			field: "void",
			flex: 0.5,
			width: 50,
			renderCell: (value) => {
				const financial = value.row;
				return isNullUndefined(props.hideVoid) && (
						<IconButton
							disabled={
								financial.status === 'VOID' ||
								user.proxyRole === 'CS' ||
								user.proxyRole === 'REC'
							}
							onClick={() => props.handleVoidClick(financial)}
							sx={{
								color: 'red',
								fontSize: '5rem',
								textAlign: 'center',
								fontWeight: 800,
							}}
						>
							<Tooltip title="Void Transaction">
								<Delete />
							</Tooltip>
						</IconButton>
				)
			}
		}]: []
	)
	]


	useEffect(() => {
		const list = financials.map((value,index) => {
			if(props.transactionMode === "Meters") {
				return {
					id: value.id,
					name: value.name,
					type: value.transactionType,
					refNo: value.referenceNo,
					invoiceOrReceiptId: value.invoiceOrReceiptId,
					openingReading: (value.transactionType === "Invoice" || value.transactionType === "Credit" || value.transactionType === "Mpesa") ? formatCurrency(value.openingReading) : "-",
					closingReading: (value.transactionType === "Invoice" || value.transactionType === "Credit" || value.transactionType === "Mpesa") ? formatCurrency(value.closingReading) : "-",
					time: formatShortDateTime(value.entryDate),
					quantity: (value.transactionType === "Invoice" || value.transactionType === "Credit" || value.transactionType === "Mpesa") ? value.quantity : "-",
					amount: value.amount
				}
			} else {
				return {
					id: value.id,
					date : value.date,
					type : value.type,
					refNo : value.id,
					refNumber : value.refNumber,
					referenceNumber: value.referenceNumber,
					details : value.details,
					amountDue : value.increase,
					amountPaid : value.decrease,
					status: value.status
				}
		}
		})
		setItemList(list)


	}, [financials])

	useEffect(() => {
		if (props.transactionMode === 'Ownership') {
			getTotalOwnershipFinancials(props.contract.id).then(response => setTotalRows(response.count))
		} else if (props.transactionMode === 'Lease') {
			getTotalLeaseFinancials(props.contract.id).then(response => setTotalRows(response.count))
		} else if (props.transactionMode === 'Client') {
			getTotalClientFinancials(props.contract.id).then(response => setTotalRows(response.count))
		} else if (props.transactionMode === 'Meters') {
			getTotalMeterFinancials(props.contract.id).then(response => setTotalRows(response.count))
		} else if (props.transactionMode === 'Meter Billing') {
			getTotalMeterBillingFinancials(props.contract.id).then(response => setTotalRows(response.count))
		} else if (props.transactionMode === 'Customer') {
			getTotalMeterBillingFinancials(props.contract.billingId).then(response => setTotalRows(response.count))
		} else if (props.transactionMode === 'Client Customer') {
			
		}
	}, [])


	useEffect(() => {
		if (
			!isNullUndefined(props.contract) &&
			!isNullUndefined(props.contract.id)
		) {
			setLoading(true)
			const pageNo = paginationModel.page
      const rowsPerPage = paginationModel.pageSize
			if (props.transactionMode === 'Ownership') {
				getOwnershipFinancials(props.contract.id, pageNo, rowsPerPage).then((response) => {
					if (response.items.length > 0) {
						let result = response.items
							.filter(
								(value, index, self) =>
									index ===
									self.findIndex(
										(t) =>
											t.id === value.id &&
											t.referenceNumber === value.referenceNumber
									)
							);
						setFinancials(result.sort(sortJSONArray("date", false)));
					}
					setCurrentBalance(response.titleBalance);
					setLoading(false)
				});
			}

			if (props.transactionMode === 'Lease') {
				getLeaseFinancials(props.contract.id, pageNo, rowsPerPage).then((response) => {
					if (response.items.length > 0) {
						let result = response.items
							.filter(
								(value, index, self) =>
									index ===
									self.findIndex(
										(t) =>
											t.id === value.id &&
											t.referenceNumber === value.referenceNumber
									)
							);
						setFinancials(result.sort(sortJSONArray("date", false)));
					}
					setCurrentBalance(response.leaseBalance);
					setLoading(false)
				});
			}

			if (props.transactionMode === 'Meter Billing') {
				getMeterBillingFinancials(props.contract.id, pageNo, rowsPerPage).then(
					(response) => {
						if (response.items.length > 0) {
							let result = response.items
								.filter(
									(value, index, self) =>
										index ===
										self.findIndex(
											(t) =>
												t.id === value.id &&
												t.referenceNumber === value.referenceNumber
										)
								);
							setFinancials(result.sort(sortJSONArray("date", false)));
						}
						setCurrentBalance(response.meterBillingBalance);
						setLoading(false)
					}
				);
			}

			if (props.transactionMode === 'Customer') {
				getMeterBillingFinancials(props.contract.billingId, pageNo, rowsPerPage).then(
					(response) => {
						if (response.items.length > 0) {
							let result = response.items
								.filter(
									(value, index, self) =>
										index ===
										self.findIndex(
											(t) =>
												t.id === value.id &&
												t.referenceNumber === value.referenceNumber
										)
								);
							setFinancials(result.sort(sortJSONArray("date", false)));
						}
						setCurrentBalance(props.contract.billingBalance);
						setLoading(false)
					}
				);
			}

			if (props.transactionMode === 'Client Customer') {
				getCustomerFinancials(props.contract.id, pageNo).then((response) => {
					if (response.items.length > 0) {
						let result = response.items
							.filter(
								(value, index, self) =>
									index ===
									self.findIndex(
										(t) =>
											t.id === value.id &&
											t.referenceNumber === value.referenceNumber
									)
							);
						setFinancials(result.sort(sortJSONArray("date", false)));
					}
					setCurrentBalance(response.customerBalance);
					setLoading(false)
				});
			}

			if (props.transactionMode === 'Client') {
				getClientFinancials(props.contract.id, pageNo, rowsPerPage).then((response) => {
					if (response.items.length > 0) {
						let result = response.items
							.filter(
								(value, index, self) =>
									index ===
									self.findIndex(
										(t) =>
											t.id === value.id &&
											t.referenceNumber === value.referenceNumber
									)
							);
						setFinancials(result.sort(sortJSONArray("date", false)));
					}
					setCurrentBalance(response.clientBalance);
					setLoading(false)
				});
			}

			if (props.transactionMode === 'Meters') {
				getMeterFinancials(props.contract.id, pageNo, rowsPerPage).then((response) => {
					if (response.items.length > 0) {
						let result = response.items
							.filter(
								(value, index, self) =>
									index ===
									self.findIndex(
										(t) =>
											t.id === value.id &&
											t.referenceNumber === value.referenceNumber
									)
							);
						setFinancials(result.sort(sortJSONArray("entryDate", false)));
						getMeterType(result);
					}
					setLoading(false)
					setCurrentBalance(response.clientBalance);
				});
			}
		}
	}, [paginationModel]);

	function handlePrintDocument(type, id) {
		if (type === 'Invoice') {
			if (props.transactionMode === 'Client') {
				//print client invoice
				printClientInvoice(id);
			} else {
				//print invoice
				printInvoice(id);
			}
		}
		if (type === 'Receipt') {
			if (props.transactionMode === 'Client') {
				//print client Receipt
				printClientReceipt(id);
			} else {
				//print Receipt
				printReceipt(id);
			}
		}
		if (type === 'Credit') {
			if (props.transactionMode === 'Client') {
				//print client Credit
				printClientCredit(id);
			} else {
				//print Credit
				printCredit(id);
			}
		}
		if (type === 'Charge') {
			if (props.transactionMode === 'Client') {
				//print client Charge
				printClientCharge(id);
			} else {
				//print Charge
				printCharge(id);
			}
		}
		if (type === 'Payment Voucher') {
			printPaymentVoucher(id);
		}
	}

	const getMeterType = (financials) => {
		if (!financials || financials.length === 0) {
			return;
		}

		const isBulkWaterMeter = financials.some((financial) => financial.meterType === 'BLK');

		setIsBulkWaterMeter(isBulkWaterMeter);
	};

	/*const showEditReadingModal = (value) => {
		setEditMeterReadingModal(true);
		setCurrentReading(value.closingReading);
		setReadingId(value.closingReadingId);
	};*/

	const closeEditReadingModal = () => {
		setEditMeterReadingModal(false);
		setCurrentReading('');
		setNewReading('');
		setReadingId('');
	};

	const submitReading = () => {
		if (isEmptyString(newReading) || isNullUndefined(newReading)) {
			issueWarnMessage('Please enter a valid value!');
			return false;
		}

		if (tryParseFloat(newReading) === tryParseFloat(currentReading)) {
			issueWarnMessage('No change in amount!');
			return false;
		}

		let sendData = {
			amount: newReading,
			readingId: readingId,
		};

		editReadings(sendData).then((response) => {
			issueResponseMessage(response);
			closeEditReadingModal();
			getMeterFinancials(props.contract.id, paginationModel.pageSize) //Reload meter transaction items
				.then((response) => {
					if (response.items.length > 0) {
						let result = response.items.filter(
							(value, index, self) =>
								index ===
								self.findIndex(
									(t) =>
										t.id === value.id &&
										t.referenceNumber === value.referenceNumber
								)
						);
						setFinancials(result);
						getMeterType(response.items);
					}
					setCurrentBalance(response.clientBalance);
				});
		});
	};

	return (
		<>
			<ResponsiveRow
				sx={{
					flexDirection: 'column',
					flexWrap: 'nowrap',
					height: { xs: '100%', lg: itemList.length > 10 ? props.height : 'auto' },
					width: { xs: '100%', lg: props.width ? props.width : '100%' },
					margin: 'auto',
					padding: '20px 30px',
					backgroundColor: '#fff',
					borderRadius: '10px',
				}}
			>
				<Typography
					variant="h5"
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', lg: 'row' },
						textAlign: 'start',
					}}
				>
					Transaction History{' '}
				</Typography>
				{props.transactionMode !== 'Meters' && (
					<ResponsiveRow
						sx={{
							display: 'flex',
							flexDirection: {xs: "column", lg: 'row'},
							alignItems: 'flex-start',
							justifyContent: {xs: 'flex-start', lg: 'space-between'},
							height: 'auto',
						}}
						id="modal-modal-title"
						// variant="h6"
						// component="h6"
					>
						<Typography variant="h6" color="primary.dark" textAlign="start">
							{props.transactionMode} Balance: KSH{' '}
							{formatCurrency(currentBalance)}
						</Typography>
					</ResponsiveRow>
				)}
				<MuiDataGrid
					alt={true}
					noSelection={true}
					dataGridColumns={
						props.transactionMode === 'Meters' ? meterColumns : columns
					}
					getRowClassName={(params) => {
						return params.row.status === "VOID"
						  ? "suspended"
						  : "";
					}}
					handlePageLoad={setPaginationModel}
					dataGridRows={itemList}
					serverPagination={true}
					loading={loading}
					height="90%"
					totalRows={totalRows ?? itemList.length}
				/>
			</ResponsiveRow>

			{editMeterReadingModal && (
				<ModalContainerModule
					size="small"
					accept={() => {
						submitReading();
					}}
					cancel={() => closeEditReadingModal()}
					openModal={editMeterReadingModal}
					modalTitle={'Edit Meter Reading'}
					acceptButtonText="Update"
				>
					<DetailsContainer sx={{ width: '100%' }} container>
						<FormControl sx={{ width: { xs: '100%', lg: '50%' } }}>
							<InputLabel>Current reading: {currentReading}</InputLabel>
							<OutlinedInput
								label={`Current reading ${currentReading}`}
								id="editMeterReading"
								placeholder={currentReading}
								value={newReading}
								sx={{
									width: { xs: '100%', md: '90%' },
								}}
								onChange={(event) => {
									const value = event.target.value;
									setNewReading(value);
								}}
								multiline
								rows={4}
								inputProps={
									<TextField sx={{ width: { xs: '100%', md: '90%' } }} />
								}
							/>
						</FormControl>
					</DetailsContainer>
				</ModalContainerModule>
			)}
		</>
	);
}
